import { useCallback, useState } from "react";
import type { StudentTestAnswer } from "p6m-p6u";
import { buildClassMemberNameMap, getClassMemberDisplayName } from "../helpers/TestReview";
import { getMembersForClass } from "../networking/schoolClasses";

/**
 * Resolves the current class member name for a result row, rather than the studentName snapshot
 * taken at test start, so a rename shows up without rewriting any row.
 */
const useClassMemberNames = () => {
    const [classMemberNamesById, setClassMemberNamesById] = useState<Map<string, string>>(new Map());

    // Rows without a classMemberId have nothing to resolve, so skip the request entirely.
    const loadClassMemberNames = useCallback(async (results: Array<StudentTestAnswer>, schoolClassId?: string) => {
        if (!schoolClassId || !results.some((result) => result.classMemberId)) {
            setClassMemberNamesById(new Map());
            return;
        }

        try {
            const classMembers = await getMembersForClass(schoolClassId);
            setClassMemberNamesById(buildClassMemberNameMap(classMembers));
        } catch (e) {
            // Names are cosmetic, so a failure must not take down the review page.
            console.error("Could not load class members, falling back to the names stored on the results", e);
            setClassMemberNamesById(new Map());
        }
    }, []);

    const resetClassMemberNames = useCallback(() => setClassMemberNamesById(new Map()), []);

    const getDisplayName = useCallback(
        (result: StudentTestAnswer) => getClassMemberDisplayName(result, classMemberNamesById),
        [classMemberNamesById]
    );

    return { getClassMemberDisplayName: getDisplayName, loadClassMemberNames, resetClassMemberNames };
};

export default useClassMemberNames;
